import { Card, Form, Select } from "antd";
import {
  useFetchCreditorSummariesQuery,
  useLazyFetchCreditorsQuery,
} from "features/creditors/agencyPortal/creditorsAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useFetchProductOfferingsQuery } from "features/productOfferings/productOfferingsAPI";
import styled from "styled-components";

const StyledForm = styled(Form)`
  max-width: 400px;
`;

export default function MultirowUploadParamsForm({ onSubmit, isFetching, providerLabel }) {
  const [form] = Form.useForm();
  const [
    fetchCreditors,
    {
      data: creditorsResponse,
      isLoading: isCreditorDetailsLoading,
      isFetching: isCreditorDetailsFetching,
    },
  ] = useLazyFetchCreditorsQuery();
  const { data: creditors, isLoading } = useFetchCreditorSummariesQuery();
  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: productOfferings } = useFetchProductOfferingsQuery();

  const selectedCreditor = creditorsResponse?.results?.[0];

  const onCreditorIdChange = async (creditorId) => {
    form.setFieldsValue({ cbrClassCode: null, productOfferingId: null });
    fetchCreditors({ filters: { creditorIds: [creditorId] } });
  };

  const onDependentSelectChange = async () => {
    const validatedFields = await form.validateFields();
    onSubmit(validatedFields);
  };

  return (
    <Card>
      <StyledForm layout="vertical" form={form} disabled={isFetching}>
        <Form.Item
          label="Client"
          name="creditorId"
          rules={[{ required: true, message: "This field is required." }]}
          extra={`Client must have ${providerLabel} integration configured`}
        >
          <Select
            showSearch
            allowClear
            popupMatchSelectWidth={false}
            filterOption={(input, option) =>
              (option.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={creditors?.map((creditor) => ({
              value: creditor.id,
              label: creditor.code ? `${creditor.name} (${creditor.code})` : creditor.name,
            }))}
            onChange={onCreditorIdChange}
            loading={isLoading}
          />
        </Form.Item>
        {selectedCreditor && (
          <>
            <Form.Item
              name="cbrClassCode"
              label="CBR Class Code"
              rules={[{ required: true, message: "Please select the CBR Class Code" }]}
            >
              <Select
                placeholder="Select the CBR Class Code"
                allowClear
                popupMatchSelectWidth={false}
                options={constants?.cbrClassCodes
                  .filter((dt) => {
                    return selectedCreditor.cbrClassCodes.includes(dt.value);
                  })
                  .map((cbrClassCodeOption) => ({
                    value: cbrClassCodeOption.value,
                    label: cbrClassCodeOption.display,
                  }))}
                onSelect={onDependentSelectChange}
                loading={isCreditorDetailsLoading || isCreditorDetailsFetching}
              />
            </Form.Item>
            <Form.Item
              name="productOfferingId"
              label="Product"
              rules={[{ required: true, message: "Please select a product" }]}
            >
              <Select
                placeholder="Select one..."
                showSearch
                optionFilterProp="label"
                allowClear
                popupMatchSelectWidth={false}
                options={productOfferings
                  ?.filter((product) =>
                    (selectedCreditor?.productOfferingIds ?? []).includes(product.id),
                  )
                  .map((product) => ({
                    value: product.id,
                    label: product.name,
                  }))}
                onSelect={onDependentSelectChange}
                loading={isCreditorDetailsLoading || isCreditorDetailsFetching}
              />
            </Form.Item>
          </>
        )}
      </StyledForm>
    </Card>
  );
}
